// apps/web/src/pages/spread/buildSpreadEvalContext.ts
//
// Phase 9 Plan 15 (GAP CLOSURE) Task 1 — pure EvalContext factory for the
// spread render path. React-free; consumed by useSpreadConstraintEvaluation
// (which wraps the Plan 09-10 useConstraintEvaluation hook with the Plan 09-06
// dispatcher underneath).
//
// CSTR-02 ("pure evaluate runs at edit time AND render time") closure: this
// factory is the render-time half. The edit-time half (Constraints.tsx) already
// computes its EvalContext inline; on the render side, SpreadRenderPane reads
// batchSets via useWatch and the loaded recipe body from TanStack Query, then
// hands them to this factory.
//
// SHAPE invariants (Phase 9 Plan 03 packages/shared/src/constraints/types.ts):
//   - widgetStates: ReadonlyMap<string, Readonly<Record<string, unknown>>>.
//     Initial mount = empty Map; subsequent renders pick up state from the
//     RecipeStateProvider via useContext.
//   - batchSetCount: number — input.batchSets.length.
//   - currentBatchSetIndex: number — caller picks the active batch set; we
//     forward as-is.
//   - currentRecipeId: string — input.recipe.id (the loaded active recipe).
//   - otherBatchSetsAllEmpty: boolean — every OTHER batchSet has no recipeId.
//   - activeModeId: string | undefined — v1 always undefined (mode pickers wire
//     in Phase 13+). MUST be set explicitly (NOT omitted) under
//     exactOptionalPropertyTypes.
//   - spreadRecipeCount: number — number of batchSets carrying a non-empty
//     recipeId string.
//
// SECURITY / DEFENSE-IN-DEPTH:
//   batchSets entries are narrowed structurally — each entry's `recipeId` is
//   accepted only when it is a string AND has length > 0. Defensive guards
//   tolerate the RHF default-value path where a brand-new batch set may carry
//   an empty string sentinel ("" or undefined).

import type {
  EvalContext,
  RecipeDefinitionV1,
} from "@the-recipe-spread/shared";

// ---------------------------------------------------------------------------
// Input shape — minimal structural surface so this factory does not bind to
// react-hook-form's FieldValues. The caller threads the loaded RecipeDefinitionV1
// body, the RHF batchSets watch, and the active index it computed.
// ---------------------------------------------------------------------------

export interface SpreadBatchSetInput {
  recipeId?: string | undefined;
}

export interface SpreadEvalContextInput {
  /**
   * The loaded recipe body (from GET /api/recipes/:id). The factory only reads
   * `recipe.id` — wider RecipeDefinitionV1 fields are passed through unused.
   */
  recipe: Pick<RecipeDefinitionV1, "title"> | { id: string };
  batchSets: ReadonlyArray<SpreadBatchSetInput>;
  /**
   * Caller-computed active batch set index. The empty-state branch in
   * SpreadRenderPane never reaches the factory; when batchSets is empty the
   * caller may still pass 0 (sentinel) — the factory tolerates it.
   */
  activeBatchSetIndex: number;
  /**
   * Optional widget-state map snapshot from RecipeStateProvider. Initial mount
   * is an empty Map; subsequent renders observe state from interactive widgets.
   */
  widgetStates?: ReadonlyMap<string, Readonly<Record<string, unknown>>>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isNonEmptyRecipeId(recipeId: string | undefined): boolean {
  return typeof recipeId === "string" && recipeId.length > 0;
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function buildSpreadEvalContext(input: SpreadEvalContextInput): EvalContext {
  const batchSets = input.batchSets;
  const batchSetCount = batchSets.length;

  // currentRecipeId — derived from the loaded recipe body. The factory
  // accepts a structural subset; the {id: string} shape is the canonical
  // surface for tests and the production wider Pick<RecipeDefinitionV1,...>
  // shape is widened down to {id} via the cast below.
  const recipeWithId = input.recipe as { id?: string };
  const currentRecipeId =
    typeof recipeWithId.id === "string" ? recipeWithId.id : "";

  // spreadRecipeCount — how many batch sets carry a non-empty recipeId.
  let spreadRecipeCount = 0;
  for (const bs of batchSets) {
    if (isNonEmptyRecipeId(bs.recipeId)) spreadRecipeCount += 1;
  }

  // otherBatchSetsAllEmpty — true iff every batchSet at an index != active is
  // empty. Vacuously true when batchSets is empty (no "other" batch sets).
  let otherBatchSetsAllEmpty = true;
  for (let i = 0; i < batchSets.length; i += 1) {
    if (i === input.activeBatchSetIndex) continue;
    const bs = batchSets[i];
    if (bs !== undefined && isNonEmptyRecipeId(bs.recipeId)) {
      otherBatchSetsAllEmpty = false;
      break;
    }
  }

  // Defaults — widgetStates omitted → empty Map. activeModeId is explicitly
  // set to undefined per exactOptionalPropertyTypes (Phase 01.1 D-15) so the
  // returned object always has the key.
  const widgetStates: ReadonlyMap<
    string,
    Readonly<Record<string, unknown>>
  > = input.widgetStates ?? new Map<string, Readonly<Record<string, unknown>>>();

  return {
    widgetStates,
    batchSetCount,
    currentBatchSetIndex: input.activeBatchSetIndex,
    currentRecipeId,
    otherBatchSetsAllEmpty,
    activeModeId: undefined,
    spreadRecipeCount,
  };
}
